import { Link } from "react-router-dom";
import { ArrowUpRight, ListChecks, Compass, CalendarHeart, CalendarPlus, LifeBuoy } from "lucide-react";
import { IMAGES } from "@/lib/site";
import { Reveal } from "@/components/motion-primitives";
import { SectionHead, usePageMeta } from "@/components/shared";

const TOOLS = [
  {
    icon: ListChecks,
    title: "The Bermuda Bucket List Challenge",
    copy: "Twenty island experiences. One challenge card. How much of Bermuda can you complete before checkout?",
    to: "/bermuda-bucket-list",
    cta: "Start the Challenge",
    image: IMAGES.pinkSand,
    testid: "guest-tool-bucket-list",
  },
  {
    icon: Compass,
    title: "How Should I Get Around Bermuda?",
    copy: "Bus, ferry, taxi, ride share or electric microcar? Sixty seconds of questions, one personal transport match.",
    to: "/guest-tools/getting-around-bermuda",
    cta: "Take the Quiz",
    image: IMAGES.hamilton,
    testid: "guest-tool-transport-quiz",
  },
  {
    icon: CalendarHeart,
    title: "Plan Your Bermuda Stay",
    copy: "Tell us who you're travelling with, what you love and where you're staying — get a personal mini-itinerary.",
    to: "/guest-tools/plan-your-stay",
    cta: "Plan My Stay",
    image: IMAGES.tobaccoBay,
    testid: "guest-tool-plan-stay",
  },
  {
    icon: CalendarPlus,
    title: "My Bermuda Calendar",
    copy: "Reservations, dinners, transfers, excursions, all-aboard times — drop every plan into Google, Apple or Outlook in one tap.",
    to: "/calendar",
    cta: "Build My Calendar",
    image: IMAGES.beach,
    testid: "guest-tool-calendar",
  },
];

export default function GuestTools() {
  usePageMeta("Guest Tools", "Free interactive tools for Bermuda visitors — the Bucket List Challenge, the getting-around quiz and a personal stay planner from The Bermuda Co-Host.");
  return (
    <main>
      <section className="mx-auto max-w-[1440px] px-6 pb-24 pt-40 lg:px-10">
        <SectionHead
          overline="Guest Tools"
          title={<>Your pocket<br /><span className="italic text-bermuda-dark">Bermuda concierge.</span></>}
          copy="Free, fun and genuinely useful — built for guests staying anywhere on the island, not just at our properties."
        />
        <div className="mt-16 grid gap-8 md:grid-cols-2 xl:grid-cols-4">
          {TOOLS.map((t, i) => (
            <Reveal key={t.title} delay={i * 0.08} className="h-full">
              <Link to={t.to} data-testid={t.testid} className="group flex h-full flex-col border border-navy/10 bg-white transition-all duration-500 hover:border-bermuda hover:shadow-2xl hover:shadow-navy/10">
                <div className="img-frame h-52 overflow-hidden">
                  <img src={t.image} alt={t.title} className="h-full w-full object-cover" loading="lazy" />
                </div>
                <div className="flex flex-1 flex-col p-8">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-pink-soft text-bermuda-dark">
                    <t.icon size={17} strokeWidth={1.5} />
                  </span>
                  <h3 className="mt-5 font-serif text-[22px] leading-tight text-navy">{t.title}</h3>
                  <p className="mt-3 flex-1 text-[13px] leading-relaxed text-navy/60">{t.copy}</p>
                  <span className="mt-6 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-navy/60 transition-colors group-hover:text-bermuda-dark">
                    {t.cta} <ArrowUpRight size={13} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2}>
          <div className="mt-14 flex flex-col items-center gap-5 bg-navy p-10 text-center text-ivory sm:flex-row sm:text-left" data-testid="guest-tools-support-card">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-bermuda/40 text-bermuda">
              <LifeBuoy size={20} strokeWidth={1.5} />
            </span>
            <div className="flex-1">
              <h3 className="font-serif text-2xl">Need a human instead?</h3>
              <p className="mt-1.5 text-sm text-ivory/65">Staying at a property we manage? Our local team is one message away.</p>
            </div>
            <Link to="/guest-support" data-testid="guest-tools-support-link" className="shrink-0 bg-bermuda px-7 py-3.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-navy transition-colors hover:bg-ivory">
              Guest Support
            </Link>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
